import React from "react";

/* ------------------------------- primitives ------------------------------- */

const Leaf = ({ x, y, r = 0, c = "#6B8E5A", s = 1 }) => (
  <g transform={`translate(${x},${y}) rotate(${r}) scale(${s})`}>
    <path d="M0,0 Q15,-22 32,-4 Q18,14 0,0 Z" fill={c} />
    <path d="M0,0 Q16,-10 32,-4" stroke="#3E5A32" strokeWidth="1" fill="none" opacity="0.45" />
  </g>
);

const Balloon = ({ x, y, c = "#F4A261", s = 1, tie = 42 }) => (
  <g transform={`translate(${x},${y}) scale(${s})`}>
    <path d={`M0,22 Q-3,${22 + tie / 2} 0,${22 + tie}`} stroke="#8B8B8B" strokeWidth="1.2" fill="none" />
    <ellipse cx="0" cy="0" rx="18" ry="22" fill={c} />
    <path d="M-3,22 L3,22 L0,26 Z" fill={c} />
    <ellipse cx="-6" cy="-7" rx="3" ry="5" fill="#FFFFFF" opacity="0.55" />
  </g>
);

const Flower = ({ x, y, c = "#F4A261", center = "#F4D35E", s = 1, r = 0 }) => (
  <g transform={`translate(${x},${y}) rotate(${r}) scale(${s})`}>
    {[0, 60, 120, 180, 240, 300].map((a) => (
      <ellipse key={a} cx="0" cy="-11" rx="6.5" ry="11" fill={c} transform={`rotate(${a})`} />
    ))}
    <circle cx="0" cy="0" r="4.5" fill={center} />
  </g>
);

const Star = ({ x, y, c = "#F4D35E", s = 1, r = 0 }) => (
  <path
    transform={`translate(${x},${y}) rotate(${r}) scale(${s})`}
    d="M0,-12 L3.5,-3.8 L12,-3.8 L5,1.5 L7.6,10 L0,5 L-7.6,10 L-5,1.5 L-12,-3.8 L-3.5,-3.8 Z"
    fill={c}
  />
);

const Heart = ({ x, y, c = "#E76F7D", s = 1, r = 0 }) => (
  <path
    transform={`translate(${x},${y}) rotate(${r}) scale(${s})`}
    d="M0,10 C-2,4 -12,0 -12,-6 C-12,-11 -7,-13 -4,-10 C-2,-8 -1,-6 0,-4 C1,-6 2,-8 4,-10 C7,-13 12,-11 12,-6 C12,0 2,4 0,10 Z"
    fill={c}
  />
);

const Paw = ({ x, y, c = "#7A5C3E", s = 1 }) => (
  <g transform={`translate(${x},${y}) scale(${s})`}>
    <ellipse cx="0" cy="4" rx="7" ry="6" fill={c} />
    <ellipse cx="-7" cy="-5" rx="3" ry="4" fill={c} />
    <ellipse cx="-2" cy="-9" rx="3" ry="4" fill={c} />
    <ellipse cx="4" cy="-9" rx="3" ry="4" fill={c} />
    <ellipse cx="8" cy="-5" rx="3" ry="4" fill={c} />
  </g>
);

const Sun = ({ x, y, c = "#F4B740", rays = "#F4D35E", s = 1 }) => (
  <g transform={`translate(${x},${y}) scale(${s})`}>
    {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
      <line key={a} x1="0" y1="-22" x2="0" y2="-15" stroke={rays} strokeWidth="2.5" strokeLinecap="round" transform={`rotate(${a})`} />
    ))}
    <circle cx="0" cy="0" r="12" fill={c} />
  </g>
);

const PalmFrond = ({ x, y, r = 0, c = "#5DAE8B", s = 1 }) => (
  <g transform={`translate(${x},${y}) rotate(${r}) scale(${s})`}>
    <path d="M0,0 Q30,-4 60,-2" stroke="#3E7A5A" strokeWidth="2" fill="none" />
    {[10, 22, 34, 46, 56].map((d, i) => (
      <g key={i} transform={`translate(${d},${-1}) rotate(${-40 + i * 4})`}>
        <path d="M0,0 Q8,-12 18,-4 Q10,2 0,0 Z" fill={c} />
      </g>
    ))}
    {[10, 22, 34, 46, 56].map((d, i) => (
      <g key={"b" + i} transform={`translate(${d},${1}) rotate(${40 - i * 4}) scale(1,-1)`}>
        <path d="M0,0 Q8,-12 18,-4 Q10,2 0,0 Z" fill={c} />
      </g>
    ))}
  </g>
);

const Grape = ({ x, y, c = "#7E4A80", leaf = "#6B8E5A", s = 1 }) => (
  <g transform={`translate(${x},${y}) scale(${s})`}>
    <path d="M-10,-20 Q-2,-22 6,-18" stroke="#3E5A32" strokeWidth="1.5" fill="none" />
    <path d="M-14,-22 Q-20,-30 -8,-32 Q0,-28 -6,-20 Z" fill={leaf} />
    {[
      [0, -8], [-6, -4], [6, -4], [-10, 2], [-2, 2], [6, 2], [10, 2],
      [-6, 8], [2, 8], [-2, 14],
    ].map(([dx, dy], i) => (
      <circle key={i} cx={dx} cy={dy} r="5" fill={c} />
    ))}
  </g>
);

const Cupcake = ({ x, y, s = 1 }) => (
  <g transform={`translate(${x},${y}) scale(${s})`}>
    <path d="M-12,0 L12,0 L8,14 L-8,14 Z" fill="#D98C8A" />
    {[-8, -3, 2, 7].map((dx) => (
      <line key={dx} x1={dx} y1="2" x2={dx} y2="13" stroke="#A85B5A" strokeWidth="0.8" />
    ))}
    <path d="M-13,0 Q-13,-12 0,-14 Q13,-12 13,0 Z" fill="#F6C1D5" />
    <circle cx="0" cy="-14" r="2.5" fill="#E76F7D" />
    <circle cx="-6" cy="-5" r="1.3" fill="#F4D35E" />
    <circle cx="5" cy="-7" r="1.3" fill="#8CC0D8" />
    <circle cx="0" cy="-3" r="1.3" fill="#E76F7D" />
  </g>
);

const Note = ({ x, y, c = "#2E3A4F", s = 1, r = 0 }) => (
  <g transform={`translate(${x},${y}) rotate(${r}) scale(${s})`}>
    <line x1="8" y1="-20" x2="8" y2="6" stroke={c} strokeWidth="2" />
    <ellipse cx="3" cy="8" rx="7" ry="5" fill={c} />
    <path d="M8,-20 Q18,-16 18,-8" stroke={c} strokeWidth="2" fill="none" />
  </g>
);

const Sparkle = ({ x, y, c = "#F4D35E", s = 1 }) => (
  <g transform={`translate(${x},${y}) scale(${s})`}>
    <path d="M0,-8 L1.2,-1.2 L8,0 L1.2,1.2 L0,8 L-1.2,1.2 L-8,0 L-1.2,-1.2 Z" fill={c} />
  </g>
);

const Feather = ({ x, y, r = 0, c = "#C9A66B", s = 1 }) => (
  <g transform={`translate(${x},${y}) rotate(${r}) scale(${s})`}>
    <path d="M0,-20 Q12,-8 8,10 Q-6,4 -10,-10 Q-4,-18 0,-20 Z" fill={c} />
    <path d="M0,-20 L8,10" stroke="#8B6F3F" strokeWidth="1" fill="none" />
  </g>
);

const Confetti = ({ x, y, c = "#F4A261", s = 1, r = 0 }) => (
  <rect transform={`translate(${x},${y}) rotate(${r}) scale(${s})`} x="-2" y="-4" width="4" height="8" fill={c} rx="1" />
);

/* ------------------------------- per-theme bands ------------------------------- */

const SVG = ({ children, h = 150 }) => (
  <svg viewBox="0 0 1200 180" preserveAspectRatio="xMidYMid meet" className="w-full" style={{ height: "auto", maxHeight: h, display: "block" }} aria-hidden="true">
    {children}
  </svg>
);

function SafariTop() {
  return (
    <SVG>
      <path d="M-20,30 Q200,70 500,40 T1220,35" stroke="#7A5C3E" strokeWidth="2.5" fill="none" />
      <path d="M-20,36 Q200,76 500,46 T1220,41" stroke="#9A7A54" strokeWidth="1.2" fill="none" opacity="0.7" />
      {[[60, 52, -30], [140, 66, 20], [220, 60, -15], [360, 48, 25], [480, 54, -20], [640, 48, 18], [780, 56, -30], [900, 50, 25], [1040, 58, -12], [1140, 52, 20]].map(([x, y, r], i) => (
        <Leaf key={i} x={x} y={y} r={r} c={i % 3 === 0 ? "#8BAE6A" : "#6B8E5A"} s={0.9} />
      ))}
      <Balloon x={200} y={96} c="#F4A261" tie={40} />
      <Balloon x={420} y={82} c="#F4D35E" tie={55} s={1.1} />
      <Balloon x={700} y={92} c="#E9C46A" tie={45} />
      <Balloon x={940} y={86} c="#D98C8A" tie={50} s={1.05} />
      <Paw x={120} y={150} c="#9A7A54" s={0.7} />
      <Paw x={320} y={158} c="#9A7A54" s={0.6} />
      <Paw x={580} y={150} c="#9A7A54" s={0.7} />
      <Paw x={820} y={160} c="#9A7A54" s={0.6} />
      <Paw x={1060} y={154} c="#9A7A54" s={0.7} />
    </SVG>
  );
}
function SafariBottom() {
  return (
    <SVG h={110}>
      <path d="M-20,140 Q300,100 600,130 T1220,125" stroke="#7A5C3E" strokeWidth="2" fill="none" />
      {[[100, 120, 20], [260, 108, -20], [420, 118, 15], [640, 112, -25], [820, 116, 18], [1040, 110, -15]].map(([x, y, r], i) => (
        <Leaf key={i} x={x} y={y} r={r} c={i % 2 === 0 ? "#6B8E5A" : "#8BAE6A"} s={0.8} />
      ))}
      <Paw x={200} y={160} c="#9A7A54" s={0.55} />
      <Paw x={540} y={160} c="#9A7A54" s={0.55} />
      <Paw x={900} y={160} c="#9A7A54" s={0.55} />
    </SVG>
  );
}

function TropicalTop() {
  return (
    <SVG>
      <PalmFrond x={20} y={60} r={-10} c="#4E9E77" s={1.4} />
      <PalmFrond x={0} y={110} r={15} c="#5DAE8B" s={1.2} />
      <PalmFrond x={1200} y={60} r={190} c="#4E9E77" s={1.4} />
      <PalmFrond x={1200} y={110} r={165} c="#5DAE8B" s={1.2} />
      <Sun x={600} y={60} c="#F4B740" rays="#F4D35E" s={1.2} />
      <Leaf x={400} y={120} r={45} c="#6B8E5A" s={0.9} />
      <Leaf x={820} y={130} r={-40} c="#6B8E5A" s={0.9} />
      <Flower x={260} y={140} c="#E76F7D" center="#F4D35E" s={0.9} />
      <Flower x={940} y={145} c="#E76F7D" center="#F4D35E" s={0.9} />
    </SVG>
  );
}
function TropicalBottom() {
  return (
    <SVG h={110}>
      <PalmFrond x={30} y={60} r={10} c="#4E9E77" s={1.2} />
      <PalmFrond x={1170} y={60} r={170} c="#4E9E77" s={1.2} />
      <Flower x={300} y={80} c="#E76F7D" center="#F4D35E" s={0.8} />
      <Flower x={900} y={80} c="#F4A261" center="#F4D35E" s={0.8} />
    </SVG>
  );
}

function GardenTop() {
  return (
    <SVG>
      <path d="M-20,80 C200,40 400,120 600,70 S1000,50 1220,90" stroke="#6B8E5A" strokeWidth="2" fill="none" />
      {[[60, 96, -20], [180, 70, 20], [310, 102, -10], [440, 76, 30], [560, 96, -20], [700, 72, 15], [830, 98, -25], [960, 70, 20], [1090, 100, -10], [1180, 80, 25]].map(([x, y, r], i) => (
        <Leaf key={i} x={x} y={y} r={r} c={i % 2 === 0 ? "#6B8E5A" : "#8BAE6A"} s={0.8} />
      ))}
      <Flower x={160} y={110} c="#E76F7D" />
      <Flower x={340} y={130} c="#F4A261" center="#E76F7D" s={1.1} />
      <Flower x={520} y={116} c="#D8A9D6" />
      <Flower x={700} y={134} c="#F4D35E" center="#E76F7D" s={1.1} />
      <Flower x={880} y={118} c="#E76F7D" />
      <Flower x={1050} y={132} c="#D98C8A" center="#F4D35E" s={1.1} />
    </SVG>
  );
}
function GardenBottom() {
  return (
    <SVG h={110}>
      <path d="M-20,130 Q300,90 600,120 T1220,120" stroke="#6B8E5A" strokeWidth="1.8" fill="none" />
      <Flower x={200} y={130} c="#E76F7D" s={0.9} />
      <Flower x={480} y={120} c="#F4A261" s={0.9} />
      <Flower x={760} y={125} c="#D8A9D6" s={0.9} />
      <Flower x={1020} y={118} c="#F4D35E" center="#E76F7D" s={0.9} />
    </SVG>
  );
}

function FestiveTop() {
  return (
    <SVG>
      <Balloon x={120} y={96} c="#E76F7D" tie={40} s={1.1} />
      <Balloon x={280} y={80} c="#F4D35E" tie={55} s={1.2} />
      <Balloon x={460} y={90} c="#8CC0D8" tie={48} s={1.1} />
      <Balloon x={640} y={78} c="#D98C8A" tie={58} s={1.25} />
      <Balloon x={820} y={94} c="#A98DCF" tie={42} s={1.1} />
      <Balloon x={1000} y={82} c="#F4A261" tie={52} s={1.2} />
      {[[70, 150], [200, 140], [360, 155], [530, 145], [700, 155], [900, 140], [1080, 152]].map(([x, y], i) => (
        <Confetti key={i} x={x} y={y} c={["#F4D35E", "#E76F7D", "#8CC0D8", "#F4A261", "#A98DCF"][i % 5]} r={i * 30} />
      ))}
      {[[160, 160], [420, 165], [680, 162], [940, 164]].map(([x, y], i) => (
        <Sparkle key={i} x={x} y={y} c="#F4D35E" />
      ))}
    </SVG>
  );
}
function FestiveBottom() {
  return (
    <SVG h={110}>
      {[[100, 60], [260, 80], [420, 65], [580, 85], [740, 60], [900, 80], [1060, 65]].map(([x, y], i) => (
        <Confetti key={i} x={x} y={y} c={["#F4D35E", "#E76F7D", "#8CC0D8", "#F4A261", "#A98DCF"][i % 5]} r={i * 45} s={1.2} />
      ))}
      {[[200, 100], [500, 110], [800, 100], [1100, 110]].map(([x, y], i) => (
        <Sparkle key={i} x={x} y={y} c="#F4D35E" s={1.2} />
      ))}
    </SVG>
  );
}

function WineTop() {
  return (
    <SVG>
      <path d="M-20,40 Q300,30 600,45 T1220,40" stroke="#6B8E5A" strokeWidth="2" fill="none" />
      <Grape x={220} y={90} c="#7E4A80" s={1.2} />
      <Grape x={460} y={100} c="#9B5E9E" s={1.3} />
      <Grape x={740} y={92} c="#5B3A6B" s={1.2} />
      <Grape x={980} y={100} c="#7E4A80" s={1.3} />
      {[[80, 70, -20], [340, 62, 20], [600, 72, -15], [870, 66, 25], [1140, 72, -10]].map(([x, y, r], i) => (
        <Leaf key={i} x={x} y={y} r={r} c="#6B8E5A" s={0.9} />
      ))}
    </SVG>
  );
}
function WineBottom() {
  return (
    <SVG h={110}>
      {[[80, 60, -20], [340, 50, 20], [600, 60, -15], [870, 54, 25], [1140, 60, -10]].map(([x, y, r], i) => (
        <Leaf key={i} x={x} y={y} r={r} c="#6B8E5A" s={0.8} />
      ))}
      <Grape x={430} y={80} c="#7E4A80" s={1} />
      <Grape x={780} y={80} c="#9B5E9E" s={1} />
    </SVG>
  );
}

function BoyTop() {
  return (
    <SVG>
      <Balloon x={160} y={90} c="#8CC0D8" tie={48} s={1.15} />
      <Balloon x={340} y={78} c="#6B9AC4" tie={58} s={1.25} />
      <Balloon x={540} y={92} c="#A8C9DD" tie={44} s={1.1} />
      <Balloon x={740} y={78} c="#8CC0D8" tie={56} s={1.25} />
      <Balloon x={940} y={92} c="#6B9AC4" tie={46} s={1.15} />
      {[[90, 160], [440, 158], [830, 162], [1080, 158]].map(([x, y], i) => (
        <Star key={i} x={x} y={y} c="#F4D35E" s={1.2} />
      ))}
    </SVG>
  );
}
function BoyBottom() {
  return (
    <SVG h={110}>
      {[[120, 80], [320, 70], [540, 80], [760, 70], [980, 80], [1120, 75]].map(([x, y], i) => (
        <Star key={i} x={x} y={y} c="#8CC0D8" s={1.1} />
      ))}
    </SVG>
  );
}

function GirlTop() {
  return (
    <SVG>
      <path d="M-20,60 C200,40 500,80 800,50 S1200,70 1220,50" stroke="#D8A9D6" strokeWidth="2" fill="none" />
      <Flower x={120} y={90} c="#E76F7D" s={1.1} />
      <Flower x={280} y={110} c="#F6C1D5" center="#E76F7D" s={1.3} />
      <Flower x={460} y={96} c="#D98C8A" s={1.1} />
      <Flower x={640} y={112} c="#F6C1D5" center="#E76F7D" s={1.35} />
      <Flower x={820} y={94} c="#E76F7D" s={1.1} />
      <Flower x={1000} y={110} c="#D98C8A" center="#F4D35E" s={1.3} />
      <Heart x={200} y={156} c="#E76F7D" s={1} />
      <Heart x={560} y={158} c="#F6C1D5" s={1.2} />
      <Heart x={900} y={154} c="#D98C8A" s={1} />
    </SVG>
  );
}
function GirlBottom() {
  return (
    <SVG h={110}>
      <Heart x={160} y={70} c="#E76F7D" s={1.1} />
      <Heart x={420} y={85} c="#F6C1D5" s={1.25} />
      <Heart x={680} y={75} c="#D98C8A" s={1.1} />
      <Heart x={940} y={82} c="#F6C1D5" s={1.2} />
      <Flower x={280} y={90} c="#E76F7D" s={0.9} />
      <Flower x={820} y={90} c="#D98C8A" s={0.9} />
    </SVG>
  );
}

function VintageTop() {
  return (
    <SVG>
      <Feather x={140} y={100} r={-30} c="#C9A66B" s={1.1} />
      <Feather x={360} y={96} r={15} c="#B58B4D" s={1.2} />
      <Feather x={600} y={102} r={-20} c="#C9A66B" s={1.15} />
      <Feather x={840} y={96} r={20} c="#B58B4D" s={1.2} />
      <Feather x={1060} y={100} r={-25} c="#C9A66B" s={1.1} />
      {[[240, 150], [480, 152], [720, 148], [960, 152]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="3.5" fill="#E9DCC6" stroke="#B58B4D" strokeWidth="0.8" />
      ))}
    </SVG>
  );
}
function VintageBottom() {
  return (
    <SVG h={110}>
      <Feather x={240} y={60} r={10} c="#C9A66B" s={0.9} />
      <Feather x={720} y={60} r={-10} c="#B58B4D" s={0.9} />
      {[[120, 80], [420, 80], [540, 80], [840, 80], [1080, 80]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="3" fill="#E9DCC6" stroke="#B58B4D" strokeWidth="0.8" />
      ))}
    </SVG>
  );
}

function DiscoTop() {
  return (
    <SVG>
      {/* disco balls */}
      {[240, 600, 960].map((cx, i) => (
        <g key={cx} transform={`translate(${cx},70)`}>
          <circle r="28" fill="#B58BD8" />
          <circle r="28" fill="url(#disco)" />
          <circle cx="-10" cy="-10" r="5" fill="white" opacity="0.5" />
        </g>
      ))}
      <defs>
        <pattern id="disco" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
          <rect width="8" height="8" fill="#9B77C6" opacity="0.5" />
          <rect x="0.5" y="0.5" width="3" height="3" fill="#E9DCF7" />
          <rect x="4.5" y="4.5" width="3" height="3" fill="#E9DCF7" />
        </pattern>
      </defs>
      <Note x={110} y={120} c="#2E3A4F" s={1.1} r={-10} />
      <Note x={400} y={130} c="#E76F7D" s={1.2} r={10} />
      <Note x={780} y={128} c="#2E3A4F" s={1.2} r={-10} />
      <Note x={1100} y={122} c="#F4A261" s={1.1} r={10} />
      {[[60, 160], [340, 160], [520, 160], [700, 160], [880, 160], [1060, 160]].map(([x, y], i) => (
        <Sparkle key={i} x={x} y={y} c="#F4D35E" s={1.2} />
      ))}
    </SVG>
  );
}
function DiscoBottom() {
  return (
    <SVG h={110}>
      <Note x={200} y={80} c="#B58BD8" s={1} r={10} />
      <Note x={500} y={70} c="#E76F7D" s={1.1} r={-10} />
      <Note x={800} y={80} c="#F4A261" s={1} r={10} />
      <Note x={1080} y={72} c="#B58BD8" s={1.1} r={-10} />
      {[[80, 90], [360, 100], [680, 100], [980, 90]].map(([x, y], i) => (
        <Sparkle key={i} x={x} y={y} c="#F4D35E" s={1.2} />
      ))}
    </SVG>
  );
}

function CandyTop() {
  return (
    <SVG>
      <Cupcake x={160} y={120} s={1.3} />
      <Cupcake x={420} y={116} s={1.4} />
      <Cupcake x={760} y={120} s={1.3} />
      <Cupcake x={1020} y={116} s={1.4} />
      {[[280, 70], [580, 60], [900, 70], [1140, 65]].map(([x, y], i) => (
        <Heart key={i} x={x} y={y} c={["#E76F7D", "#F6C1D5", "#D98C8A"][i % 3]} s={1.1} />
      ))}
      {[[80, 90], [340, 80], [680, 88], [1100, 84]].map(([x, y], i) => (
        <Sparkle key={i} x={x} y={y} c="#F4D35E" s={1.1} />
      ))}
    </SVG>
  );
}
function CandyBottom() {
  return (
    <SVG h={110}>
      <Heart x={200} y={70} c="#E76F7D" s={1.2} />
      <Heart x={500} y={80} c="#F6C1D5" s={1.35} />
      <Heart x={800} y={70} c="#D98C8A" s={1.2} />
      <Heart x={1060} y={76} c="#F6C1D5" s={1.3} />
      {[[100, 95], [360, 100], [660, 100], [960, 95]].map(([x, y], i) => (
        <Sparkle key={i} x={x} y={y} c="#F4D35E" s={1.1} />
      ))}
    </SVG>
  );
}

/* Elegant / default — no illustration, just a clean gold line ornament so it still looks intentional */
function ElegantTop() {
  return (
    <SVG h={70}>
      <line x1="120" y1="40" x2="560" y2="40" stroke="#C9A66B" strokeWidth="1" />
      <g transform="translate(600,40)">
        <circle r="4" fill="#C9A66B" />
        <circle r="10" stroke="#C9A66B" strokeWidth="0.8" fill="none" />
      </g>
      <line x1="640" y1="40" x2="1080" y2="40" stroke="#C9A66B" strokeWidth="1" />
    </SVG>
  );
}
function ElegantBottom() { return <ElegantTop />; }

/* ------------------------------- theme routing ------------------------------- */

function pick(themeName, customTheme) {
  const t = (themeName || "").toLowerCase();
  const ct = (customTheme || "").toLowerCase();
  const any = (...ks) => ks.some((k) => t.includes(k) || ct.includes(k));

  if (any("safari", "animal", "jungle", "woodland", "storybook")) return { Top: SafariTop, Bottom: SafariBottom };
  if (any("tropical", "beach", "palm", "luau")) return { Top: TropicalTop, Bottom: TropicalBottom };
  if (any("garden", "floral", "flower", "romantic", "spring", "bloom")) return { Top: GardenTop, Bottom: GardenBottom };
  if (any("wine", "trattoria", "dinner", "candle", "italian")) return { Top: WineTop, Bottom: WineBottom };
  if (any("disco", "70s", "neon", "music")) return { Top: DiscoTop, Bottom: DiscoBottom };
  if (any("vintage", "classic", "rustic")) return { Top: VintageTop, Bottom: VintageBottom };
  if (any("boy")) return { Top: BoyTop, Bottom: BoyBottom };
  if (any("girl")) return { Top: GirlTop, Bottom: GirlBottom };
  if (any("candy", "sweet", "sugar", "valentine", "love")) return { Top: CandyTop, Bottom: CandyBottom };
  if (any("birthday", "cake", "festive", "gold", "celebratory", "celebration", "party")) return { Top: FestiveTop, Bottom: FestiveBottom };
  return { Top: ElegantTop, Bottom: ElegantBottom };
}

export default function ThemeBand({ theme, customTheme, variant = "top" }) {
  const { Top, Bottom } = pick(theme, customTheme);
  const Comp = variant === "top" ? Top : Bottom;
  return (
    <div data-testid={`theme-band-${variant}`} className="w-full">
      <Comp />
    </div>
  );
}
